import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { OnboardingTask } from "@forgepoint/shared";
import { useOnboardingStore } from "./onboardingStore";
import { TaskChecklistRow } from "./TaskChecklistRow";

interface TaskChecklistProps {
  title: string;
  tasks: OnboardingTask[];
  canInteract: boolean;
  userNames: Map<string, string>;
  showProgress?: boolean;
  groupBySection?: boolean;
  onOpenTask?: (taskId: string) => void;
}

/**
 * A card of onboarding tasks with check-off, optionally grouped by section
 * and headed by a completion progress bar. Row clicks open the task detail
 * sheet via `onOpenTask`.
 */
export function TaskChecklist({
  title,
  tasks,
  canInteract,
  userNames,
  showProgress = false,
  groupBySection = false,
  onOpenTask,
}: TaskChecklistProps) {
  const taskStateById = useOnboardingStore((state) => state.taskStateById);
  const toggleTask = useOnboardingStore((state) => state.toggleTask);

  const completedCount = tasks.filter((task) => task.completed).length;
  const sections = useMemo(() => {
    if (!groupBySection) {
      return [{ label: null, tasks }];
    }
    const bySection = new Map<string, OnboardingTask[]>();
    tasks.forEach((task) => {
      const key = task.section ?? "";
      bySection.set(key, [...(bySection.get(key) ?? []), task]);
    });
    return [...bySection.entries()].map(([label, sectionTasks]) => ({
      label: label || null,
      tasks: sectionTasks,
    }));
  }, [tasks, groupBySection]);

  if (tasks.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-base">{title}</CardTitle>
          <span className="text-sm text-muted-foreground">
            {completedCount}/{tasks.length}
          </span>
        </div>
        {showProgress && (
          <Progress value={(completedCount / tasks.length) * 100} />
        )}
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        {sections.map((section) => (
          <div key={section.label ?? "default"}>
            {section.label && (
              <div className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {section.label}
              </div>
            )}
            {section.tasks.map((task) => (
              <TaskChecklistRow
                key={task.id}
                task={task}
                canInteract={canInteract}
                isToggling={taskStateById.get(task.id)?.isToggling ?? false}
                userNames={userNames}
                onToggle={toggleTask}
                onOpen={onOpenTask ? () => onOpenTask(task.id) : undefined}
              />
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
